import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Eye, EyeOff, MapPin, Calendar, Users, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { WorkshopListingEditor } from './WorkshopListingEditor';
import { format, parseISO } from 'date-fns';

interface WorkshopListing {
  id: string;
  slug: string;
  title: string;
  location_name: string;
  latitude: number;
  longitude: number;
  price_display: string;
  max_participants: number | null;
  workshop_type: string;
  description_html: string;
  external_url: string;
  is_active: boolean;
  created_at: string;
  workshop_dates: Array<{ id: string; start_date: string; end_date: string; notes: string | null; sort_order: number }>;
  workshop_photos: Array<{ id: string; photo_url: string; alt_text: string | null; sort_order: number }>;
}

interface Props {
  leaderId: string;
  startWithEditor?: boolean;
  onEditorChange?: (open: boolean) => void;
}

const TYPE_LABELS: Record<string, string> = {
  astrophotography: 'Astrophotography',
  deep_sky: 'Deep Sky',
  landscape: 'Landscape',
  nature: 'Nature',
  mixed: 'Mixed',
};

export const WorkshopListingGrid: React.FC<Props> = ({ leaderId, startWithEditor, onEditorChange }) => {
  const [listings, setListings] = useState<WorkshopListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingListing, setEditingListing] = useState<WorkshopListing | null>(null);

  useEffect(() => {
    if (startWithEditor) {
      setEditingListing(null);
      setEditorOpen(true);
      onEditorChange?.(false);
    }
  }, [startWithEditor]);

  useEffect(() => {
    loadListings();
  }, [leaderId]);

  const loadListings = async () => {
    try {
      const { data, error } = await supabase
        .from('workshop_listings')
        .select('*, workshop_dates(*), workshop_photos(*)')
        .eq('leader_id', leaderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (err) {
      console.error('Error loading listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (listing: WorkshopListing) => {
    try {
      const { error } = await supabase
        .from('workshop_listings')
        .update({ is_active: !listing.is_active })
        .eq('id', listing.id);
      if (error) throw error;
      toast.success(listing.is_active ? 'Workshop hidden' : 'Workshop published');
      loadListings();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const deleteListing = async (listing: WorkshopListing) => {
    if (!confirm(`Delete "${listing.title}"? This cannot be undone.`)) return;
    try {
      const { error } = await supabase
        .from('workshop_listings')
        .delete()
        .eq('id', listing.id);
      if (error) throw error;
      toast.success('Workshop deleted');
      loadListings();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getNextDate = (dates: WorkshopListing['workshop_dates']) => {
    const now = new Date().toISOString().split('T')[0];
    const future = dates
      .filter(d => d.end_date >= now)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    return future[0] || dates.sort((a, b) => b.start_date.localeCompare(a.start_date))[0];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-foreground">Your Workshops</h2>
        <Button onClick={() => { setEditingListing(null); setEditorOpen(true); }} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Workshop
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-48" />
            </Card>
          ))}
        </div>
      ) : listings.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground mb-4">
              No workshops yet. Add your first workshop listing.
            </p>
            <Button onClick={() => { setEditingListing(null); setEditorOpen(true); }} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Workshop
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {listings.map(listing => {
            const heroPhoto = listing.workshop_photos?.sort((a, b) => a.sort_order - b.sort_order)[0];
            const nextDate = getNextDate(listing.workshop_dates || []);

            return (
              <Card key={listing.id} className={!listing.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-0">
                  {/* Hero image */}
                  {heroPhoto && (
                    <div className="h-40 overflow-hidden rounded-t-lg bg-[#0f1923]">
                      <img src={heroPhoto.photo_url} alt={heroPhoto.alt_text || listing.title} className="w-full h-full object-contain" />
                    </div>
                  )}

                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-foreground">{listing.title}</h3>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                          <MapPin className="w-3 h-3" />
                          {listing.location_name}
                        </div>
                      </div>
                      <Badge variant={listing.is_active ? 'default' : 'secondary'}>
                        {listing.is_active ? 'Active' : 'Hidden'}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      {nextDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(nextDate.start_date), 'MMM d')} – {format(parseISO(nextDate.end_date), 'MMM d, yyyy')}
                        </span>
                      )}
                      {listing.max_participants && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {listing.max_participants} max
                        </span>
                      )}
                      {listing.price_display && (
                        <span className="font-medium text-foreground">{listing.price_display}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Badge variant="outline" className="text-xs">{TYPE_LABELS[listing.workshop_type] || listing.workshop_type}</Badge>
                      <span className="text-xs text-muted-foreground">{listing.workshop_dates?.length || 0} date{(listing.workshop_dates?.length || 0) !== 1 ? 's' : ''}</span>
                      <span className="text-xs text-muted-foreground">{listing.workshop_photos?.length || 0} photo{(listing.workshop_photos?.length || 0) !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="flex items-center gap-1 pt-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingListing(listing); setEditorOpen(true); }} className="gap-1">
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleActive(listing)} className="gap-1">
                        {listing.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {listing.is_active ? 'Hide' : 'Show'}
                      </Button>
                      <a href={`/workshops/${listing.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="gap-1">
                          <Eye className="w-3 h-3" /> View
                        </Button>
                      </a>
                      <Button variant="ghost" size="sm" onClick={() => deleteListing(listing)} className="gap-1 text-destructive hover:text-destructive">
                        <Trash2 className="w-3 h-3" /> Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <WorkshopListingEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        leaderId={leaderId}
        existingListing={editingListing}
        onSave={loadListings}
      />
    </div>
  );
};
